import { Image as TiptapImage, type ImageOptions } from "@tiptap/extension-image"
import * as z from "zod"
import { shouldIncludeAttributes } from "../../util/markdown-options"
import { mdImage } from "../../util/markdown-template"
import {
  formatXmlAttributes,
  parseXmlAttributes,
  XML_ATTRIBUTES_SOURCE,
  xmlInt,
} from "../../util/markdown-xml-attributes"
import { toTiptapAttributes } from "../../util/render-data"

export const ImageAttrsSchema = z.object({
  src: z.string().nullable().default(null),
  alt: z.string().nullable().default(null),
  title: z.string().nullable().default(null),
  width: z.int().nullable().default(null),
  uploadId: z.int().nullable().default(null),
})

export type ImageAttributes = z.input<typeof ImageAttrsSchema>

// Attributes of a Markdown `<img>` tag. uploadId is editor-only state, so it isn't one.
// XML attributes are strings, so width parses from its digits. A field that
// doesn't parse is dropped rather than failing the image.
const ImageMarkdownAttrsSchema = z.object({
  src: z.string(),
  alt: z.string().optional().catch(undefined),
  title: z.string().optional().catch(undefined),
  width: xmlInt().optional().catch(undefined),
})

// Like a CommonMark HTML block, the tag has a line to itself.
const IMAGE_TAG = new RegExp(`^ {0,3}<img(${XML_ATTRIBUTES_SOURCE})\\s*/?>[ \\t]*(?:\\n|$)`, "i")

const parseImageAttributes = (img: Element): Partial<ImageAttributes> => ({
  src: img.getAttribute("src"),
  alt: img.getAttribute("alt"),
  title: img.getAttribute("title"),
  width: img.hasAttribute("width") ? Number.parseInt(img.getAttribute("width")!) : null,
})

function buildImageHTMLAttributes(attrs: ImageAttributes): Record<string, string> {
  const result: Record<string, string> = { src: attrs.src || "" }

  if (attrs.alt) result.alt = attrs.alt
  if (attrs.title) result.title = attrs.title
  if (attrs.width) result.width = attrs.width + ""

  return result
}

export const ImageNodeContentExtension = TiptapImage.extend({
  group: "block widget",

  addOptions() {
    return {
      ...this.parent?.(),
      inline: false,
    } as ImageOptions
  },

  addAttributes: () => toTiptapAttributes(ImageAttrsSchema, { uploadId: { rendered: false } }),

  parseHTML() {
    return [
      {
        tag: "figure",
        getAttrs: (node) => {
          const img = node.querySelector("img")
          if (!img) return false

          return parseImageAttributes(img)
        },
      },
      {
        tag: "img[src]",
        getAttrs: (node) => {
          if (node.closest("figure")) return false

          return parseImageAttributes(node)
        },
      },
    ]
  },

  renderHTML({ node }) {
    const { src, alt, title, width } = node.attrs
    return ["img", buildImageHTMLAttributes({ src, alt, title, width })]
  },

  markdownTokenName: "image",
  // Plain `![alt](src)` images are left to marked's own image tokenizer.
  markdownTokenizer: {
    name: "image",
    level: "block",
    start: () => -1,
    tokenize(src) {
      const match = IMAGE_TAG.exec(src)
      if (!match) return
      const attributes = parseXmlAttributes(match[1], ImageMarkdownAttrsSchema)
      if (!attributes) return
      return { type: "image", raw: match[0], attributes }
    },
  },
  parseMarkdown: (token) => ({
    type: "image",
    attrs: ImageAttrsSchema.parse(
      token.attributes ?? { src: token.href, alt: token.text || null, title: token.title },
    ),
  }),
  renderMarkdown: (node) => {
    const { src, alt, uploadId: _uploadId, ...rest } = ImageAttrsSchema.parse(node.attrs)
    const attributes = shouldIncludeAttributes() ? formatXmlAttributes(rest) : ""
    // Without attributes, a plain Markdown image says the same thing.
    if (!attributes) return mdImage(alt || "", src || "")
    return `<img${formatXmlAttributes({ src, alt })}${attributes} />`
  },
})
